// Admin > Marketing: vendor leads loaded from a directory CSV, grouped by the app's services.
// Select leads (or every lead matching the filters), send an invitation by email / SMS / WhatsApp,
// and convert those who accept into vendor accounts with a temporary password.
// While marketing messages are switched off (Admin > Company Details) every message is only recorded as 'held'.
import React, { useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Box, Paper, Typography, Stack, Chip, Button, TextField, MenuItem, Alert, Grid, Checkbox, Table, TableHead,
  TableRow, TableCell, TableBody, TablePagination, TableContainer, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControlLabel, Drawer, IconButton, Divider, Autocomplete, Tooltip, Link, Tabs, Tab,
  CircularProgress, InputAdornment, LinearProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SendIcon from '@mui/icons-material/Send';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StarIcon from '@mui/icons-material/Star';
import {
  VENDOR_LEADS_QUERY, VENDOR_LEAD_STATS_QUERY, VENDOR_LEAD_IMPORTS_QUERY, VENDOR_LEAD_OUTREACH_QUERY,
  IMPORT_VENDOR_LEADS_MUTATION, SEND_VENDOR_OUTREACH_MUTATION, SET_VENDOR_LEAD_STATUS_MUTATION,
  VENDOR_LEAD_INVITE_LINK_MUTATION, ACCEPT_VENDOR_LEADS_MUTATION, MESSAGE_TEMPLATES_QUERY, VENDOR_LEAD_CREDENTIALS_QUERY,
  RESTORE_VENDOR_LEAD_MUTATION, CREATE_VENDOR_LEAD_MUTATION, ALL_SERVICE_CATEGORIES_QUERY,
} from '../graphql/queries';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import VendorPasswordDialog from '../components/VendorPasswordDialog';

const STATUSES = [
  ['new', 'New', 'default'], ['contacted', 'Contacted', 'info'], ['accepted', 'Accepted', 'warning'],
  ['converted', 'Vendor', 'success'], ['declined', 'Declined', 'default'], ['do_not_contact', 'Do not contact', 'error'],
  ['excluded', 'Excluded', 'default'],
];
const STATUS = Object.fromEntries(STATUSES.map(([k, l, c]) => [k, { label: l, color: c }]));
const CONTACT = [['', 'Any contact'], ['mobile', 'Has a mobile'], ['phone', 'Has a phone'], ['email', 'Has an email'], ['none', 'No contact details']];
const SORTS = [['name', 'Name'], ['city', 'City'], ['rating', 'Rating'], ['price', 'Starting price'], ['contacted', 'Last contacted'], ['recent', 'Recently updated']];
const CHANNELS = [['email', 'Email'], ['sms', 'SMS'], ['whatsapp', 'WhatsApp']];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const phoneText = (n) => (n ? `+${n.slice(0, 2)} ${n.slice(2)}` : '');
const rupees = (n) => (n ? `₹${Number(n).toLocaleString('en-IN')}` : '');
const copy = (text) => { try { navigator.clipboard.writeText(text); } catch (e) { /* ignore */ } };
// Where the login details went: sent | failed | held (sending off) | skipped (no template / connection / mobile)
const SEND_COLOR = { sent: 'success', failed: 'error', held: 'warning', skipped: 'default' };
function DeliveryChips({ r }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.5}>
      {[['Email', r.emailStatus], ['WhatsApp', r.whatsappStatus], ['SMS', r.smsStatus]].filter(([, st]) => st).map(([k, st]) => (
        <Chip key={k} size="small" variant="outlined" color={SEND_COLOR[st] || 'default'} label={`${k}: ${st}`} />
      ))}
    </Stack>
  );
}
const VENDOR_STATE = { deactivated: 'Vendor account deactivated', deleted: 'Vendor account deleted' };

const EMPTY_FILTER = { search: '', services: [], states: [], cities: [], statuses: [], contact: '' };

function StatTile({ label, value, sub }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 140px' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight={700}>{value ?? '—'}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

// ── Upload ─────────────────────────────────────────────────────────────────────
function UploadDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [run, { loading, error }] = useMutation(IMPORT_VENDOR_LEADS_MUTATION);
  const input = useRef();
  const close = () => { setFile(null); setResult(null); onClose(); };
  const go = async () => {
    const csv = await file.text();
    const { data } = await run({ variables: { csv, fileName: file.name } });
    setResult(data.importVendorLeads);
    onDone();
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Upload vendor list (CSV)</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          The file needs the columns <b>Vendor / Hall Name</b> and <b>Category</b>. Category and Type are mapped to our services, phone
          numbers are normalised, and a business already in the list is updated rather than added twice. Uploading sends no messages.
        </Typography>
        <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); }} />
        <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => input.current?.click()}>{file ? file.name : 'Choose CSV file'}</Button>
        {file && <Typography variant="caption" display="block" mt={0.5}>{(file.size / 1024 / 1024).toFixed(1)} MB</Typography>}
        {loading && <LinearProgress sx={{ mt: 2 }} />}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message}</Alert>}
        {result && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {result.rows} rows: <b>{result.inserted}</b> new, <b>{result.updated}</b> updated, {result.duplicates} duplicates in the file, {result.invalid} without a name.
            <br />{result.withMobile} with a mobile, {result.withPhone} with any phone, {result.withEmail} with an email; {result.noContact} have no contact details;
            {' '}{result.excluded} excluded (government offices).
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && <Button variant="contained" disabled={!file || loading} onClick={go}>Upload</Button>}
      </DialogActions>
    </Dialog>
  );
}

// ── Outreach ───────────────────────────────────────────────────────────────────
function OutreachDialog({ open, onClose, target, count, settings, onDone }) {
  const tpl = settings?.template || {};
  const [channels, setChannels] = useState(['whatsapp', 'sms', 'email']);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [chosen, setChosen] = useState({ whatsapp: '', sms: '', email: '' });
  const [run, { loading, error }] = useMutation(SEND_VENDOR_OUTREACH_MUTATION);
  const { data: tplData } = useQuery(MESSAGE_TEMPLATES_QUERY, { variables: { purpose: 'vendor_invite' }, fetchPolicy: 'cache-and-network' });
  const templatesOf = (c) => (tplData?.messageTemplates || []).filter(t => t.channel === c && t.isActive);
  const close = () => { setResult(null); onClose(); };
  const toggle = (c) => setChannels(cs => (cs.includes(c) ? cs.filter(x => x !== c) : [...cs, c]));
  const go = async () => {
    const templates = Object.fromEntries(Object.entries(chosen).filter(([c, id]) => id && channels.includes(c)));
    const { data } = await run({ variables: { ...target, channels, subject: subject || null, message: message || null, templates } });
    setResult(data.sendVendorOutreach);
    onDone();
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Invite {count.toLocaleString('en-IN')} vendor{count === 1 ? '' : 's'} to join</DialogTitle>
      <DialogContent>
        {!settings?.sendingEnabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Marketing messages are <b>switched off</b>. Messages are recorded as <b>held</b> against each lead and nothing is sent. To send for real,
            switch them on in <Link href="/dashboard/admin/company">Admin → Company Details</Link> and connect email, WhatsApp and SMS (Message Templates).
          </Alert>
        )}
        <Stack direction="row" spacing={1} mb={2}>
          {CHANNELS.map(([k, l]) => (
            <FormControlLabel key={k} control={<Checkbox checked={channels.includes(k)} onChange={() => toggle(k)} />} label={l} />
          ))}
        </Stack>
        <Grid container spacing={1.5} mb={2}>
          {CHANNELS.filter(([k]) => channels.includes(k)).map(([k, l]) => {
            const list = templatesOf(k);
            const picked = list.find(t => t.id === chosen[k]);
            const auto = list.find(t => t.sendable) || list[0];
            return (
              <Grid item xs={12} sm={4} key={k}>
                <TextField select fullWidth size="small" label={`${l} template`} value={chosen[k]} onChange={e => setChosen(c => ({ ...c, [k]: e.target.value }))}
                  helperText={k === 'email' ? (picked ? '' : 'Or the message below')
                    : (picked || auto) ? ((picked || auto).sendable ? 'Approved' : k === 'whatsapp' ? 'Not approved yet: sent by hand from WhatsApp' : 'Not approved yet: SMS will fail')
                      : k === 'whatsapp' ? 'No template: sent by hand from WhatsApp' : 'No SMS template yet'}>
                  <MenuItem value="">{k === 'email' ? 'Message below' : `Automatic${auto ? ` (${auto.name})` : ''}`}</MenuItem>
                  {list.map(t => <MenuItem key={t.id} value={t.id}>{t.name} · {t.language}{t.sendable ? '' : ` (${t.status})`}</MenuItem>)}
                </TextField>
              </Grid>
            );
          })}
        </Grid>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Manage templates and their approval in <Link href="/dashboard/admin/message-templates">Admin → Message Templates</Link>.
          The subject and message below are used for email without a template, and for WhatsApp sent by hand.
        </Typography>
        <TextField label="Email subject" fullWidth size="small" value={subject} onChange={e => setSubject(e.target.value)} placeholder={tpl.subject} sx={{ mb: 2 }} />
        <TextField label="Message" fullWidth multiline minRows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder={tpl.message}
          helperText={`Leave empty for the standard message. Placeholders: ${(tpl.placeholders || []).map(p => `{{${p}}}`).join(' ')}. {{link}} is each vendor's own invitation link.`} />
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          SMS and WhatsApp go to mobile numbers only; email to the leads that have one. Leads marked Vendor, Declined, Do not contact or Excluded are skipped.
          Promotional SMS in India needs a DLT-registered sender and template (TRAI); every message offers "Do not contact me".
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message}</Alert>}
        {result && (
          <Alert severity={result.failed ? 'warning' : 'success'} sx={{ mt: 2 }}>
            {result.leads} leads · held {result.held} · sent {result.sent} · WhatsApp to send by hand {result.manual} · failed {result.failed}
            {' '}· no number/email {result.skipped} · not contactable {result.notContactable}
            {result.templates && <><br />Templates: {Object.entries(result.templates).map(([c, t]) => `${c}: ${t ? t.name : 'message below'}`).join(' · ')}</>}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button variant="contained" startIcon={<SendIcon />} disabled={!channels.length || loading} onClick={go}>
            {settings?.sendingEnabled ? 'Send' : 'Record (held)'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Add a vendor by hand ───────────────────────────────────────────────────────
// A business that is not in any directory file. It becomes a lead like the imported ones (invite
// it, or accept it as a vendor straight away, which creates the account and sends the login details).
const EMPTY_LEAD = {
  name: '', services: [], contactPerson: '', phone: '', email: '', website: '', address: '', area: '', city: '', district: '',
  state: '', pincode: '', startingPrice: '', about: '', notes: '', allowDuplicate: false,
};
function AddLeadDialog({ onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_LEAD);
  const { data: catData } = useQuery(ALL_SERVICE_CATEGORIES_QUERY, { variables: { activeOnly: true } });
  const [create, { loading, error, reset }] = useMutation(CREATE_VENDOR_LEAD_MUTATION);
  const cats = catData?.allServiceCategories || [];
  const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); reset(); };
  const duplicateEmail = /already has the email/.test(error?.message || '');
  const ready = form.name.trim() && form.services.length && (form.phone.trim() || form.email.trim());
  const save = async (thenAccept) => {
    const input = { ...form, startingPrice: form.startingPrice === '' ? null : Number(form.startingPrice) };
    const res = await create({ variables: { input } }).catch(() => null);
    if (res) onCreated(res.data.createVendorLead, thenAccept);
  };
  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add a vendor</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          A business that is not in a directory file. It is added as a lead: send it an invitation, or accept it as a vendor now to create
          its account and send the login details to the email and mobile given here.
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Business name *" value={form.name} onChange={set('name')} /></Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete multiple size="small" options={cats} getOptionLabel={(c) => c.displayName}
              value={cats.filter(c => form.services.includes(c.name))}
              onChange={(_, v) => { setForm(f => ({ ...f, services: v.map(c => c.name) })); reset(); }}
              renderInput={(params) => <TextField {...params} label="Services *" placeholder={form.services.length ? '' : 'What the vendor offers'} />} />
          </Grid>
          <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Contact person" value={form.contactPerson} onChange={set('contactPerson')} /></Grid>
          <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Mobile / phone" value={form.phone} onChange={set('phone')} placeholder="98400 12345" helperText="A mobile can get WhatsApp and SMS" /></Grid>
          <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Email" type="email" value={form.email} onChange={set('email')} helperText="Becomes the user ID if accepted" /></Grid>
          <Grid item xs={12}><TextField size="small" fullWidth label="Address" value={form.address} onChange={set('address')} /></Grid>
          <Grid item xs={6} sm={3}><TextField size="small" fullWidth label="Area" value={form.area} onChange={set('area')} /></Grid>
          <Grid item xs={6} sm={3}><TextField size="small" fullWidth label="City / town" value={form.city} onChange={set('city')} /></Grid>
          <Grid item xs={6} sm={2}><TextField size="small" fullWidth label="District" value={form.district} onChange={set('district')} /></Grid>
          <Grid item xs={6} sm={2}><TextField size="small" fullWidth label="State" value={form.state} onChange={set('state')} /></Grid>
          <Grid item xs={6} sm={2}><TextField size="small" fullWidth label="Pincode" value={form.pincode} onChange={e => { setForm(f => ({ ...f, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })); reset(); }} /></Grid>
          <Grid item xs={6} sm={4}><TextField size="small" fullWidth label="Starting price (₹)" type="number" value={form.startingPrice} onChange={set('startingPrice')} inputProps={{ min: 0 }} /></Grid>
          <Grid item xs={12} sm={8}><TextField size="small" fullWidth label="Website" value={form.website} onChange={set('website')} /></Grid>
          <Grid item xs={12}><TextField size="small" fullWidth multiline minRows={2} label="About the business" value={form.about} onChange={set('about')} /></Grid>
          <Grid item xs={12}><TextField size="small" fullWidth label="Notes (only for admins)" value={form.notes} onChange={set('notes')} /></Grid>
        </Grid>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message}</Alert>}
        {duplicateEmail && (
          <FormControlLabel sx={{ mt: 1 }} control={<Checkbox checked={form.allowDuplicate} onChange={e => setForm(f => ({ ...f, allowDuplicate: e.target.checked }))} />}
            label="Add anyway: it is a different business" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="outlined" disabled={!ready || loading} onClick={() => save(false)}>Add lead</Button>
        <Button variant="contained" startIcon={<PersonAddIcon />} disabled={!ready || !form.email.trim() || loading} onClick={() => save(true)}>
          Add and accept as vendor
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Accept (admin) ─────────────────────────────────────────────────────────────
// The admin accepts one or more leads: each becomes a vendor account for its services. The user ID
// (email) and temporary password are shown here once, so the admin can hand them over.
const STEP_FORM = 'form';
function AcceptDialog({ ids, onClose, onDone }) {
  const { data, loading: loadingLeads } = useQuery(VENDOR_LEADS_QUERY, {
    variables: { filter: { ids }, limit: 100 }, fetchPolicy: 'network-only',
  });
  const [forms, setForms] = useState({});
  const [results, setResults] = useState(null);
  const [run, { loading, error }] = useMutation(ACCEPT_VENDOR_LEADS_MUTATION);
  const leads = (data?.vendorLeads?.rows || []).filter(l => l.status !== 'converted');
  const already = (data?.vendorLeads?.rows || []).length - leads.length;
  const formOf = (l) => forms[l.id] || {
    email: l.acceptedEmail || l.email || '', phone: l.phone ? phoneText(l.phone) : '', contactPerson: l.contactPerson || '',
  };
  const set = (l, k, v) => setForms(f => ({ ...f, [l.id]: { ...formOf(l), [k]: v } }));
  const ready = leads.filter(l => formOf(l).email.trim());
  const created = (results || []).filter(r => r.tempPassword);
  const asText = created.map(r => `${r.name}\nServices: ${r.services.join(', ')}\nUser ID: ${r.email}\nTemporary password: ${r.tempPassword}`).join('\n\n');
  const download = () => {
    const cell = (v) => `"${String(v || '').replace(/"/g, '""')}"`;
    const csv = [['Vendor', 'Services', 'User ID', 'Temporary password'], ...created.map(r => [r.name, r.services.join('; '), r.email, r.tempPassword])]
      .map(r => r.map(cell).join(',')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `vendor-logins-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  const go = async () => {
    const items = ready.map(l => ({ id: l.id, ...formOf(l) }));
    const { data: d } = await run({ variables: { items } });
    setResults(d.acceptVendorLeads);
    onDone();
  };
  const step = results ? 'done' : STEP_FORM;

  return (
    <Dialog open onClose={step === 'done' ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{step === 'done' ? 'Vendor accounts created' : `Accept ${ids.length === 1 ? 'this lead' : `${ids.length} leads`} as vendors`}</DialogTitle>
      <DialogContent>
        {loadingLeads && <LinearProgress />}
        {step === STEP_FORM && !loadingLeads && (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Each lead gets a vendor account for its services (the email is the user ID) with a temporary password, and a draft listing
              from the directory details. Leads without an email are skipped: enter one to include them. The email and mobile entered here are
              saved on the account and the login details are sent to them by email, WhatsApp and SMS. The vendor accepts the Vendor
              Agreement when they first sign in.
            </Typography>
            {ids.length > 100 && <Alert severity="warning" sx={{ mb: 2 }}>Only the first 100 selected leads are shown. Accept the rest in another batch.</Alert>}
            {already > 0 && <Alert severity="info" sx={{ mb: 2 }}>{already} selected lead(s) are already vendors and are left out.</Alert>}
            {!leads.length && <Alert severity="info">No leads to accept.</Alert>}
            {leads.map(l => (
              <Paper key={l.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>{l.name}</Typography>
                <Typography variant="caption" color="text.secondary">{[l.city, l.state].filter(Boolean).join(', ')}</Typography>
                <Grid container spacing={1} mt={0.5}>
                  <Grid item xs={12} sm={5}><TextField size="small" fullWidth label="Email (user ID) *" value={formOf(l).email} onChange={e => set(l, 'email', e.target.value)} /></Grid>
                  <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Phone *" value={formOf(l).phone} onChange={e => set(l, 'phone', e.target.value)} /></Grid>
                  <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Contact person" value={formOf(l).contactPerson} onChange={e => set(l, 'contactPerson', e.target.value)} /></Grid>
                </Grid>
              </Paper>
            ))}
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error.message}</Alert>}
          </>
        )}
        {step === 'done' && (
          <>
            {created.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>Share these login details with each vendor.</b> You can also see them later on the lead (Login details) until the vendor sets their own password.{' '}
                {created.some(r => [r.emailStatus, r.whatsappStatus, r.smsStatus].includes('sent'))
                  ? 'They were also sent to the vendors (see where each went below).' : 'They were not sent (sending is off, or no approved template), so share them with each vendor yourself.'}
                {' '}Vendors should then set their own password with "Forgot password".
              </Alert>
            )}
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Vendor</TableCell><TableCell>Services</TableCell><TableCell>User ID</TableCell><TableCell>Temporary password</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {results.map(r => (
                  <TableRow key={r.leadId}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>{r.services.map(s => <Chip key={s} size="small" variant="outlined" label={s} />)}</Stack>
                    </TableCell>
                    {r.error ? (
                      <TableCell colSpan={2}><Typography variant="body2" color="error">{r.error}</Typography></TableCell>
                    ) : (
                      <>
                        <TableCell><b>{r.email}</b>{r.tempPassword && <DeliveryChips r={r} />}</TableCell>
                        <TableCell>
                          {r.tempPassword ? (
                            <Stack direction="row" alignItems="center">
                              <b style={{ fontFamily: 'monospace', fontSize: 15 }}>{r.tempPassword}</b>
                              <IconButton size="small" onClick={() => copy(`User ID: ${r.email}\nTemporary password: ${r.tempPassword}`)}><ContentCopyIcon fontSize="inherit" /></IconButton>
                            </Stack>
                          ) : <Typography variant="caption" color="text.secondary">Already a vendor</Typography>}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'done' && created.length > 0 && (
          <>
            <Button startIcon={<ContentCopyIcon />} onClick={() => copy(asText)}>Copy all</Button>
            <Button onClick={download}>Download CSV</Button>
          </>
        )}
        <Button onClick={onClose}>{step === 'done' ? 'Done' : 'Cancel'}</Button>
        {step === STEP_FORM && (
          <Button variant="contained" startIcon={<PersonAddIcon />} disabled={loading || loadingLeads || !ready.length} onClick={go}>
            {loading ? 'Creating…' : `Create ${ready.length} vendor account${ready.length === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// A converted lead whose vendor account was deactivated or deleted: set the vendor up again. A
// deactivated account is switched back on with a new temporary password; a deleted one is
// replaced by a new account. The login details go to the email and mobile entered here.
function RestoreDialog({ lead, onClose, onDone }) {
  const [form, setForm] = useState({
    email: lead.acceptedEmail || lead.email || '', phone: lead.phone ? phoneText(lead.phone) : '', contactPerson: lead.contactPerson || '',
  });
  const [run, { data, loading, error }] = useMutation(RESTORE_VENDOR_LEAD_MUTATION);
  const r = data?.restoreVendorLead;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const go = async () => {
    const res = await run({ variables: { id: lead.id, ...form } }).catch(() => null);
    if (res) onDone();
  };
  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set up {lead.name} again</DialogTitle>
      <DialogContent>
        {!r && (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {lead.vendorState === 'deactivated'
                ? 'The vendor account is switched back on with a new temporary password.'
                : 'The old account was deleted: a new vendor account is created with a draft listing.'}
              {' '}The email is the user ID; the login details are sent to the email and mobile below.
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={5}><TextField size="small" fullWidth label="Email (user ID) *" value={form.email} onChange={set('email')} /></Grid>
              <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Mobile *" value={form.phone} onChange={set('phone')} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Contact person" value={form.contactPerson} onChange={set('contactPerson')} /></Grid>
            </Grid>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message}</Alert>}
          </>
        )}
        {r && (
          <Alert severity="success">
            <Typography variant="body2" fontWeight={600}>{r.reactivated ? 'The vendor account is active again.' : 'A new vendor account was created.'}</Typography>
            <Typography variant="body2">User ID: <b>{r.email}</b></Typography>
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">Temporary password: <b style={{ fontFamily: 'monospace', fontSize: 15 }}>{r.tempPassword}</b></Typography>
              <IconButton size="small" onClick={() => copy(`User ID: ${r.email}\nTemporary password: ${r.tempPassword}`)}><ContentCopyIcon fontSize="inherit" /></IconButton>
            </Stack>
            <DeliveryChips r={r} />
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{r ? 'Done' : 'Cancel'}</Button>
        {!r && <Button variant="contained" disabled={loading || !form.email.trim()} onClick={go}>{loading ? 'Setting up…' : 'Set up and send login details'}</Button>}
      </DialogActions>
    </Dialog>
  );
}

// The vendor's user ID and temporary password, until the vendor changes it (each look-up is audited)
function LoginDetails({ lead, onChanged }) {
  const [show, { data, loading, error }] = useLazyQuery(VENDOR_LEAD_CREDENTIALS_QUERY, { fetchPolicy: 'network-only' });
  const [changing, setChanging] = useState(false);
  const c = data?.vendorLeadCredentials;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, my: 1 }}>
      <Typography variant="subtitle2" mb={0.5}>Login details</Typography>
      <Typography variant="body2">User ID: <b>{c?.email || lead.acceptedEmail || '—'}</b></Typography>
      {!c && (lead.tempPasswordActive ? (
        <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
          <Typography variant="body2">Temporary password: ••••••••••</Typography>
          <Button size="small" disabled={loading} onClick={() => show({ variables: { leadId: lead.id } })}>{loading ? 'Loading…' : 'Show'}</Button>
        </Stack>
      ) : <Typography variant="body2" color="text.secondary" mt={0.5}>The vendor has set their own password.</Typography>)}
      {c?.status === 'temporary' && (
        <>
          <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5}>
            <Typography variant="body2">Temporary password: <b style={{ fontFamily: 'monospace', fontSize: 15 }}>{c.tempPassword}</b></Typography>
            <IconButton size="small" onClick={() => copy(`User ID: ${c.email}\nTemporary password: ${c.tempPassword}`)}><ContentCopyIcon fontSize="inherit" /></IconButton>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Issued {fmt(c.issuedAt)} · viewed {c.viewCount}× (recorded in the audit log). It disappears once the vendor changes their password.
          </Typography>
        </>
      )}
      {c && c.status !== 'temporary' && <Typography variant="body2" color="text.secondary" mt={0.5}>The vendor has set their own password; the temporary one no longer works.</Typography>}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error.message}</Alert>}
      <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => setChanging(true)}>Change password</Button>
      {changing && (
        <VendorPasswordDialog vendorUserId={lead.convertedUserId} name={lead.name} email={c?.email || lead.acceptedEmail}
          onClose={() => setChanging(false)}
          onDone={() => { show({ variables: { leadId: lead.id } }); if (onChanged) onChanged(); }} />
      )}
    </Paper>
  );
}

// ── Lead details ───────────────────────────────────────────────────────────────
function LeadDrawer({ lead, labels, onClose, onConvert, onStatus, onChanged }) {
  const { data, loading } = useQuery(VENDOR_LEAD_OUTREACH_QUERY, { variables: { leadId: lead?.id }, skip: !lead, fetchPolicy: 'network-only' });
  const [link, setLink] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [getLink, { loading: linking }] = useMutation(VENDOR_LEAD_INVITE_LINK_MUTATION);
  if (!lead) return null;
  const st = STATUS[lead.status] || STATUS.new;
  const Row = ({ k, v }) => (v ? <Typography variant="body2" sx={{ mb: 0.5 }}><Box component="span" color="text.secondary">{k}: </Box>{v}</Typography> : null);
  const inviteLink = link || lead.inviteLink;
  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, p: 2.5 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h6" fontWeight={700}>{lead.name}</Typography>
          {lead.alternateName && <Typography variant="body2" color="text.secondary">{lead.alternateName}</Typography>}
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Stack>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap my={1}>
        <Chip size="small" label={st.label} color={st.color} />
        {lead.services.map(s => <Chip key={s} size="small" variant="outlined" label={labels[s] || s} />)}
        {lead.premium && <Chip size="small" color="warning" variant="outlined" label="Premium listing" />}
      </Stack>
      <Row k="Directory category" v={[lead.categoryRaw, lead.typeRaw].filter(Boolean).join(' · ')} />
      <Row k="Contact person" v={lead.contactPerson} />
      <Row k="Phones" v={lead.phones.map(phoneText).join(', ') || null} />
      <Row k="Phone as listed" v={lead.phoneRaw} />
      <Row k="Email" v={lead.email} />
      <Row k="Address" v={[lead.address, lead.area, lead.city, lead.district, lead.state, lead.pincode].filter(Boolean).join(', ')} />
      <Row k="Rating" v={lead.rating ? `${lead.rating} ★ (${lead.reviews || 0} reviews)` : null} />
      <Row k="Starting price" v={rupees(lead.startingPrice)} />
      <Row k="Source" v={lead.source} />
      <Stack direction="row" spacing={1} my={1}>
        {lead.website && <Link href={lead.website} target="_blank" rel="noopener" variant="body2">Website</Link>}
        {lead.listingUrl && <Link href={lead.listingUrl} target="_blank" rel="noopener" variant="body2">Listing</Link>}
        {lead.mapsLink && <Link href={lead.mapsLink} target="_blank" rel="noopener" variant="body2">Map</Link>}
        {lead.facebook && <Link href={lead.facebook} target="_blank" rel="noopener" variant="body2">Facebook</Link>}
      </Stack>
      {lead.about && <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 1 }}>{lead.about}</Typography>}
      {lead.acceptedAt && <Alert severity="success" sx={{ mb: 1 }}>Accepted on {fmt(lead.acceptedAt)} with {lead.acceptedEmail}.</Alert>}
      {lead.status === 'converted' && VENDOR_STATE[lead.vendorState] && (
        <Alert severity="warning" sx={{ my: 1 }}
          action={<Button color="inherit" size="small" onClick={() => setRestoring(true)}>Set up again</Button>}>
          {VENDOR_STATE[lead.vendorState]}. Set the vendor up again to send new login details.
        </Alert>
      )}
      {lead.status === 'converted' && lead.vendorState !== 'deleted' && <LoginDetails key={lead.id} lead={lead} onChanged={onChanged} />}
      {restoring && <RestoreDialog lead={lead} onClose={() => setRestoring(false)} onDone={() => { if (onChanged) onChanged(); }} />}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap my={1}>
        {lead.status !== 'converted' && <Button size="small" variant="contained" startIcon={<PersonAddIcon />} onClick={() => onConvert([lead.id])}>Accept as vendor</Button>}
        <Button size="small" variant="outlined" startIcon={<LinkIcon />} disabled={linking}
          onClick={async () => { const { data: d } = await getLink({ variables: { id: lead.id } }); setLink(d.vendorLeadInviteLink); copy(d.vendorLeadInviteLink); }}>
          {inviteLink ? 'Copy invitation link' : 'Get invitation link'}
        </Button>
        {lead.status !== 'converted' && (
          <TextField select size="small" label="Set status" value="" onChange={e => (e.target.value === 'accepted' ? onConvert([lead.id]) : onStatus([lead.id], e.target.value))} sx={{ minWidth: 150 }}>
            {STATUSES.filter(([k]) => !['converted', lead.status].includes(k)).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </TextField>
        )}
      </Stack>
      {inviteLink && (
        <Typography variant="caption" sx={{ wordBreak: 'break-all' }} color="text.secondary">
          {inviteLink} {link && '(copied)'}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography fontWeight={700} mb={1}>Outreach ({lead.contactCount})</Typography>
      {loading && <CircularProgress size={20} />}
      {data?.vendorLeadOutreach?.length === 0 && <Typography variant="body2" color="text.secondary">Not contacted yet.</Typography>}
      {data?.vendorLeadOutreach?.map(o => (
        <Paper key={o.id} variant="outlined" sx={{ p: 1, mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={o.channel} />
            <Chip size="small" variant="outlined" label={o.status} color={o.status === 'sent' ? 'success' : o.status === 'failed' ? 'error' : 'default'} />
            {o.purpose === 'credentials' && <Chip size="small" variant="outlined" label="login details" />}
            <Typography variant="caption" color="text.secondary">{fmt(o.createdAt)}</Typography>
          </Stack>
          {o.recipient && <Typography variant="caption" display="block">To {o.channel === 'email' ? o.recipient : phoneText(o.recipient)}{o.sentBy ? ` · by ${o.sentBy}` : ''}</Typography>}
          {o.error && <Typography variant="caption" display="block" color="text.secondary">{o.error}</Typography>}
          {o.status === 'manual' && o.message?.startsWith('https://wa.me/') && <Link href={o.message} target="_blank" rel="noopener" variant="caption">Open in WhatsApp</Link>}
        </Paper>
      ))}
    </Drawer>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function AdminMarketingPage() {
  const [tab, setTab] = useState(0);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selected, setSelected] = useState(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [upload, setUpload] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(null); // the lead just added by hand
  const [outreach, setOutreach] = useState(false);
  const [convert, setConvert] = useState(null);
  const [open, setOpen] = useState(null);
  const [notice, setNotice] = useState(null);

  const gqlFilter = useMemo(() => {
    const f = {};
    Object.entries(filter).forEach(([k, v]) => { if (Array.isArray(v) ? v.length : v) f[k] = v; });
    return f;
  }, [filter]);
  const { data: statsData, refetch: refetchStats } = useQuery(VENDOR_LEAD_STATS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data, loading, error, refetch } = useQuery(VENDOR_LEADS_QUERY, {
    variables: { filter: gqlFilter, limit: rowsPerPage, offset: page * rowsPerPage, sort }, fetchPolicy: 'cache-and-network',
  });
  const { data: importsData, refetch: refetchImports } = useQuery(VENDOR_LEAD_IMPORTS_QUERY, { skip: tab !== 1 });
  const [setStatus] = useMutation(SET_VENDOR_LEAD_STATUS_MUTATION);

  const stats = statsData?.vendorLeadStats;
  const settings = statsData?.marketingSettings;
  const labels = useMemo(() => Object.fromEntries((stats?.byService || []).map(s => [s.service, s.label])), [stats]);
  const rows = data?.vendorLeads?.rows || [];
  const total = data?.vendorLeads?.total || 0;
  const cityOptions = useMemo(() => [...new Set((stats?.cities || [])
    .filter(c => !filter.states.length || filter.states.includes(c.state)).map(c => c.city))], [stats, filter.states]);

  const refreshAll = () => { refetch(); refetchStats(); if (tab === 1) refetchImports(); };
  const change = (patch) => { setFilter(f => ({ ...f, ...patch })); setPage(0); setSelected(new Set()); setAllMatching(false); };
  const toggleService = (s) => change({ services: filter.services.includes(s) ? filter.services.filter(x => x !== s) : [...filter.services, s] });
  const pageIds = rows.map(r => r.id);
  const allOnPage = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const toggleRow = (id) => { setAllMatching(false); setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const togglePage = () => { setAllMatching(false); setSelected(s => { const n = new Set(s); pageIds.forEach(id => (allOnPage ? n.delete(id) : n.add(id))); return n; }); };
  const selectionCount = allMatching ? total : selected.size;
  const target = allMatching ? { filter: gqlFilter } : { ids: [...selected] };

  const changeStatus = async (ids, status) => {
    const { data: d } = await setStatus({ variables: { ids, status } });
    setNotice(`${d.setVendorLeadStatus} lead(s) set to ${STATUS[status]?.label || status}.`);
    setOpen(null);
    refreshAll();
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Marketing: vendor leads</Typography>
          <Typography variant="body2" color="text.secondary">Invite businesses from vendor directories to join as vendors. Those who accept get a vendor account.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<AddBusinessIcon />} onClick={() => setAdding(true)}>Add vendor</Button>
        <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setUpload(true)}>Upload CSV</Button>
      </Stack>

      {settings && !settings.sendingEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" href="/dashboard/admin/company">Switch on</Button>}>
          Marketing messages are switched off (Admin → Company Details): invitations and login emails are recorded as <b>held</b>, and nothing is
          sent to vendors. You can still copy a lead's invitation link, or create the vendor account yourself and share the login details.
        </Alert>
      )}
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap mb={2}>
        <StatTile label="Leads" value={stats?.total?.toLocaleString('en-IN')} sub={(stats?.byState || []).slice(0, 3).map(s => `${s.state} ${s.count}`).join(' · ')} />
        <StatTile label="With a mobile" value={stats?.withMobile?.toLocaleString('en-IN')} sub="SMS / WhatsApp" />
        <StatTile label="With any phone" value={stats?.withPhone?.toLocaleString('en-IN')} />
        <StatTile label="With email" value={stats?.withEmail?.toLocaleString('en-IN')} />
        <StatTile label="Vendors signed up" value={(stats?.byStatus || []).find(s => s.status === 'converted')?.count || 0}
          sub={`${(stats?.byStatus || []).find(s => s.status === 'contacted')?.count || 0} contacted`} />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Leads" />
        <Tab label="Uploads" />
      </Tabs>

      {tab === 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" mb={1}>Services</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mb={2}>
              {(stats?.byService || []).map(s => (
                <Tooltip key={s.service} title={`${s.withMobile} with a mobile · ${s.withEmail} with email · ${s.converted} vendors`}>
                  <Chip label={`${s.label} · ${s.count}`} onClick={() => toggleService(s.service)}
                    color={filter.services.includes(s.service) ? 'primary' : 'default'}
                    variant={filter.services.includes(s.service) ? 'filled' : 'outlined'} />
                </Tooltip>
              ))}
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={3}>
                <TextField size="small" fullWidth placeholder="Name, phone, area, pincode…" value={search}
                  onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') change({ search }); }} onBlur={() => search !== filter.search && change({ search })}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete multiple size="small" options={(stats?.byState || []).map(s => s.state)} value={filter.states}
                  onChange={(_, v) => change({ states: v, cities: [] })} renderInput={p => <TextField {...p} label="State" />} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete multiple size="small" options={cityOptions} value={filter.cities}
                  onChange={(_, v) => change({ cities: v })} renderInput={p => <TextField {...p} label="City / district" />} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField select size="small" fullWidth label="Status" value={filter.statuses[0] || ''} onChange={e => change({ statuses: e.target.value ? [e.target.value] : [] })}>
                  <MenuItem value="">Any status</MenuItem>
                  {STATUSES.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField select size="small" fullWidth label="Contact" value={filter.contact} onChange={e => change({ contact: e.target.value })}>
                  {CONTACT.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            {JSON.stringify(filter) !== JSON.stringify(EMPTY_FILTER) && (
              <Button size="small" sx={{ mt: 1 }} onClick={() => { setSearch(''); change(EMPTY_FILTER); }}>Clear filters</Button>
            )}
          </Paper>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} mb={1}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {selectionCount ? <b>{selectionCount.toLocaleString('en-IN')} selected</b> : `${total.toLocaleString('en-IN')} leads`}
              {allOnPage && !allMatching && total > pageIds.length && (
                <Button size="small" onClick={() => setAllMatching(true)}>Select all {total.toLocaleString('en-IN')} matching</Button>
              )}
              {selectionCount > 0 && <Button size="small" onClick={() => { setSelected(new Set()); setAllMatching(false); }}>Clear selection</Button>}
            </Typography>
            <TextField select size="small" label="Sort" value={sort} onChange={e => setSort(e.target.value)} sx={{ minWidth: 150 }}>
              {SORTS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
            </TextField>
            <Button variant="contained" startIcon={<SendIcon />} disabled={!selectionCount} onClick={() => setOutreach(true)}>Send invitation</Button>
            <TextField select size="small" label="Set status" value="" disabled={!selected.size || allMatching} onChange={e => (e.target.value === 'accepted' ? setConvert([...selected]) : changeStatus([...selected], e.target.value))} sx={{ minWidth: 140 }}>
              {STATUSES.filter(([k]) => k !== 'converted').map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
            </TextField>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 1 }}>{error.message}</Alert>}
          <Paper variant="outlined">
            {loading && <LinearProgress />}
            <TableContainer sx={{ maxHeight: '65vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"><Checkbox checked={allOnPage} indeterminate={!allOnPage && pageIds.some(id => selected.has(id))} onChange={togglePage} /></TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Services</TableCell>
                    <TableCell>Place</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Rating / price</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(l => {
                    const st = STATUS[l.status] || STATUS.new;
                    return (
                      <TableRow key={l.id} hover selected={allMatching || selected.has(l.id)} sx={{ cursor: 'pointer' }} onClick={() => setOpen(l)}>
                        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                          <Checkbox checked={allMatching || selected.has(l.id)} onChange={() => toggleRow(l.id)} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{l.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{l.typeRaw || l.categoryRaw}{l.contactPerson ? ` · ${l.contactPerson}` : ''}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {l.services.map(s => <Chip key={s} size="small" variant="outlined" label={labels[s] || s} />)}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{l.city || l.district || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{[l.area, l.state].filter(Boolean).join(', ')}</Typography>
                        </TableCell>
                        <TableCell>
                          {l.phone && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {l.hasMobile ? <PhoneIphoneIcon sx={{ fontSize: 15 }} color="success" /> : <PhoneIcon sx={{ fontSize: 15 }} color="action" />}
                              <Typography variant="body2">{phoneText(l.phone)}</Typography>
                              {l.phones.length > 1 && <Typography variant="caption" color="text.secondary">+{l.phones.length - 1}</Typography>}
                            </Stack>
                          )}
                          {l.email && <Stack direction="row" spacing={0.5} alignItems="center"><EmailIcon sx={{ fontSize: 15 }} color="action" /><Typography variant="caption">{l.email}</Typography></Stack>}
                          {!l.phone && !l.email && <Typography variant="caption" color="text.secondary">No contact details</Typography>}
                        </TableCell>
                        <TableCell>
                          {l.rating ? <Stack direction="row" alignItems="center" spacing={0.25}><StarIcon sx={{ fontSize: 15, color: '#f5a623' }} /><Typography variant="body2">{l.rating}</Typography><Typography variant="caption" color="text.secondary">({l.reviews || 0})</Typography></Stack> : null}
                          <Typography variant="caption">{rupees(l.startingPrice)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={st.label} color={st.color} />
                          {l.tempPasswordActive && <Chip size="small" variant="outlined" color="warning" label="Temp password" sx={{ mt: 0.5, display: 'flex', width: 'fit-content' }} />}
                          {VENDOR_STATE[l.vendorState] && <Chip size="small" variant="outlined" color="error" label={l.vendorState === 'deleted' ? 'Account deleted' : 'Account deactivated'} sx={{ mt: 0.5, display: 'flex', width: 'fit-content' }} />}
                          {l.contactCount > 0 && <Typography variant="caption" display="block" color="text.secondary">{l.contactCount}× · {fmt(l.lastContactedAt)}</Typography>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && !rows.length && (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      {stats?.total ? 'No leads match these filters.' : 'No leads yet: upload a vendor CSV to start.'}
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[25, 50, 100, 200]}
              onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
          </Paper>
        </>
      )}

      {tab === 1 && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>File</TableCell><TableCell>Uploaded</TableCell><TableCell>Rows</TableCell><TableCell>New</TableCell><TableCell>Updated</TableCell><TableCell>Skipped</TableCell><TableCell>Contacts</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {(importsData?.vendorLeadImports || []).map(i => (
                <TableRow key={i.id}>
                  <TableCell>{i.fileName}</TableCell>
                  <TableCell>{fmt(i.createdAt)}<Typography variant="caption" display="block" color="text.secondary">{i.importedBy}</Typography></TableCell>
                  <TableCell>{i.totalRows}</TableCell>
                  <TableCell>{i.inserted}</TableCell>
                  <TableCell>{i.updated}</TableCell>
                  <TableCell>{i.skipped}</TableCell>
                  <TableCell><Typography variant="caption">{i.summary?.withMobile} mobile · {i.summary?.withPhone} phone · {i.summary?.withEmail} email</Typography></TableCell>
                </TableRow>
              ))}
              {!importsData?.vendorLeadImports?.length && <TableRow><TableCell colSpan={7} align="center">No uploads yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>
      )}

      <UploadDialog open={upload} onClose={() => setUpload(false)} onDone={refreshAll} />
      {adding && (
        <AddLeadDialog onClose={() => setAdding(false)}
          onCreated={(lead, thenAccept) => {
            setAdding(false); refreshAll();
            if (thenAccept) setConvert([lead.id]); else setAdded(lead);
          }} />
      )}
      {added && (
        <Dialog open onClose={() => setAdded(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{added.name} added</DialogTitle>
          <DialogContent><Typography variant="body2">It is now a lead. Send it an invitation from the list, or accept it as a vendor now.</Typography></DialogContent>
          <DialogActions>
            <Button onClick={() => { setOpen(added); setAdded(null); }}>Open lead</Button>
            <Button variant="contained" onClick={() => { setConvert([added.id]); setAdded(null); }}>Accept as vendor</Button>
          </DialogActions>
        </Dialog>
      )}
      {outreach && (
        <OutreachDialog open onClose={() => { setOutreach(false); setSelected(new Set()); setAllMatching(false); }}
          target={target} count={selectionCount} settings={settings} onDone={refreshAll} />
      )}
      {convert && <AcceptDialog ids={convert} onClose={() => { setConvert(null); setSelected(new Set()); }} onDone={() => { setOpen(null); refreshAll(); }} />}
      {open && <LeadDrawer lead={open} labels={labels} onClose={() => setOpen(null)} onConvert={(ids) => setConvert(ids)} onStatus={changeStatus} onChanged={refreshAll} />}
    </Box>
  );
}
